import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Stethoscope } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DoctorLogin = () => {
  const navigate = useNavigate();
  const [hospitalCode, setHospitalCode] = useState('');
  const [doctorId, setDoctorId] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!hospitalCode.trim()) {
      toast.error('Please enter hospital ID');
      return;
    }

    // Validate hospital ID format (8 characters: STATE(2) + CITY(3) + RANDOM(3))
    if (hospitalCode.trim().length !== 8) {
      toast.error('Hospital ID must be exactly 8 characters (e.g., TSHYD446)');
      return;
    }
    
    if (!doctorId.trim()) {
      toast.error('Please enter your doctor ID');
      return;
    }

    if (!password.trim()) {
      toast.error('Please enter your password');
      return;
    }

    setLoading(true);
    
    try {
      // First, verify hospital ID exists
      let hospital = null;
      try {
        const hospitalResponse = await axios.get(`${API}/hospitals/by-code/${hospitalCode.trim().toUpperCase()}`);
        hospital = hospitalResponse.data;
      } catch (error) {
        toast.error('Invalid hospital ID');
        setLoading(false);
        return;
      }

      // Auto-prepend "DOC" prefix if not present
      let fullDoctorId = doctorId.trim();
      if (!fullDoctorId.toUpperCase().startsWith('DOC')) {
        fullDoctorId = 'DOC' + fullDoctorId;
      }
      
      // Try to find doctor by ID and hospital
      const response = await axios.get(`${API}/doctors?hospital_id=${hospital.id}`);
      const doctor = response.data.find(d => d.id === fullDoctorId);
      
      if (doctor) {
        // Verify password
        if (doctor.password !== password) {
          // Log failed login attempt
          try {
            await axios.post(`${API}/security/login-event`, {
              user_id: fullDoctorId,
              user_name: doctor.name || fullDoctorId,
              role: 'doctor',
              hospital_id: hospital.id,
              status: 'failed',
              failure_reason: 'Invalid password'
            });
          } catch (logError) {
            console.error('Failed to log login event:', logError);
          }
          toast.error('Invalid password');
          setLoading(false);
          return;
        }
        
        // Log successful login
        let loginId = null;
        try {
          const loginResponse = await axios.post(`${API}/security/login-event`, {
            user_id: fullDoctorId,
            user_name: doctor.name,
            role: 'doctor',
            hospital_id: hospital.id,
            status: 'success'
          });
          loginId = loginResponse.data.login_id;
        } catch (logError) {
          console.error('Failed to log login event:', logError);
        }
        
        const userData = { ...doctor, role: 'doctor', loginId };
        
        // Store user data and hospital info in localStorage
        localStorage.setItem('healthcareUser', JSON.stringify(userData));
        localStorage.setItem('currentHospital', JSON.stringify(hospital));
        
        toast.success(`Welcome to ${hospital.name}, Dr. ${doctor.name}!`);
        navigate('/doctor-dashboard');
      } else {
        // Log failed login - user not found
        try {
          await axios.post(`${API}/security/login-event`, {
            user_id: fullDoctorId,
            user_name: fullDoctorId,
            role: 'doctor',
            hospital_id: hospital.id,
            status: 'failed',
            failure_reason: 'Doctor not found in hospital'
          });
        } catch (logError) {
          console.error('Failed to log login event:', logError);
        }
        toast.error('Doctor not found in this hospital. Please check your Doctor ID');
        setLoading(false);
        return;
      }
      
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full mb-4">
            <Stethoscope className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{fontFamily: 'Space Grotesk'}}>
            Doctor Portal
          </h1>
          <p className="text-base text-gray-600">Sign in to manage patient records</p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleLogin} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="hospitalCode" className="text-base">
                Hospital ID
              </Label>
              <Input
                id="hospitalCode"
                type="text"
                placeholder="Enter hospital ID (e.g., TSHYD304)"
                value={hospitalCode}
                onChange={(e) => setHospitalCode(e.target.value.toUpperCase().slice(0, 8))}
                maxLength={8}
                required
                className="h-12 text-base"
              />
              <p className="text-xs text-gray-500">8 character code from your hospital admin</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="doctorId" className="text-base">
                Doctor ID
              </Label>
              <Input
                id="doctorId"
                type="text"
                placeholder="Enter your doctor ID"
                value={doctorId}
                onChange={(e) => setDoctorId(e.target.value)}
                required
                className="h-12 text-base"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-base">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 text-base"
              />
            </div>

            <div className="space-y-3">
              <Button
                type="submit"
                className="w-full h-12 text-base bg-purple-500 hover:bg-purple-600 text-white"
                disabled={loading}
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t text-center space-y-2">
            <p className="text-sm text-gray-600">
              Need help?{' '}
              <button
                onClick={() => navigate('/')}
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                Go to Home
              </button>
            </p>
            <p className="text-sm text-gray-600">
              Other Healthcare Staff?{' '}
              <button
                onClick={() => navigate('/healthcare-login')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Staff Portal
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorLogin;
